import React, { useState } from "react";
import {
  Users, UserPlus, Instagram, Send, MessageCircle, Trash2, ExternalLink,
  Loader2, Search, Check,
} from "lucide-react";
import { EmptyState } from "./Cards";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DoozyAd Influencers — extracted from DoozyAdTab.js (iter151 refactor).
 * Behaviour preserved exactly, all data-testids kept for E2E parity.
 */

export const NICHES = ["lifestyle", "party_planning", "home_decor", "couples", "young_parents", "wedding", "kids_birthday", "food_lifestyle", "fashion", "travel"];
export const STATUSES = ["not_contacted", "dm_drafted", "dm_sent", "replied", "interested", "collab", "passed"];
export const STATUS_COLORS = {
  not_contacted: "bg-gray-100 text-gray-700",
  dm_drafted: "bg-amber-100 text-amber-700",
  dm_sent: "bg-blue-100 text-blue-700",
  replied: "bg-violet-100 text-violet-700",
  interested: "bg-pink-100 text-pink-700",
  collab: "bg-emerald-100 text-emerald-700",
  passed: "bg-rose-100 text-rose-700",
};

export const InfluencersPanel = ({ influencers, statusFilter, setStatusFilter, authAxios, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ handle: "", name: "", niche: "lifestyle", follower_count: "", location_city: "", notes: "" });
  const [adding, setAdding] = useState(false);

  const submitAdd = async () => {
    if (!form.handle.trim()) return;
    setAdding(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/influencers`, {
        ...form,
        follower_count: form.follower_count ? parseInt(form.follower_count, 10) : null,
      });
      setForm({ handle: "", name: "", niche: "lifestyle", follower_count: "", location_city: "", notes: "" });
      setShowAdd(false);
      await onRefresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Add failed");
    }
    setAdding(false);
  };

  return (
    <div className="space-y-4" data-testid="doozyad-influencers-panel">
      <div className="bg-gradient-to-br from-pink-50 to-violet-50 border border-pink-200 rounded-2xl p-5">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-1">
          <UserPlus size={16} className="text-pink-600" /> Influencer outreach tracker
        </h3>
        <p className="text-xs text-[#64748B] mb-3">
          Add IG handles → AI generates personalized cold-DMs you can copy + send manually → track replies.
          30K-friendly cohort — you stay in control of who receives what.
        </p>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowAdd(!showAdd)}
            className="px-4 py-2 bg-pink-600 text-white text-xs font-semibold rounded-xl flex items-center gap-2"
            data-testid="doozyad-influencer-add-toggle">
            <UserPlus size={13} /> {showAdd ? "Cancel" : "Add an influencer"}
          </button>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-xs border border-gray-200 rounded-xl bg-white"
            data-testid="doozyad-influencer-status-filter">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {showAdd && (
          <div className="mt-4 grid sm:grid-cols-2 gap-2 bg-white p-3 rounded-xl border border-pink-100" data-testid="doozyad-influencer-add-form">
            <input placeholder="IG handle (no @)" value={form.handle}
              onChange={(e) => setForm({ ...form, handle: e.target.value })}
              className="px-3 py-1.5 text-xs border border-gray-200 rounded-md outline-none focus:border-pink-400"
              data-testid="doozyad-influencer-handle-input" />
            <input placeholder="Name (optional)" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="px-3 py-1.5 text-xs border border-gray-200 rounded-md outline-none focus:border-pink-400" />
            <select value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })}
              className="px-3 py-1.5 text-xs border border-gray-200 rounded-md bg-white">
              {NICHES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <input type="number" placeholder="Follower count (e.g. 18000)" value={form.follower_count}
              onChange={(e) => setForm({ ...form, follower_count: e.target.value })}
              className="px-3 py-1.5 text-xs border border-gray-200 rounded-md outline-none focus:border-pink-400" />
            <input placeholder="City (e.g. Bangalore)" value={form.location_city}
              onChange={(e) => setForm({ ...form, location_city: e.target.value })}
              className="px-3 py-1.5 text-xs border border-gray-200 rounded-md outline-none focus:border-pink-400" />
            <input placeholder="Notes (optional)" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="px-3 py-1.5 text-xs border border-gray-200 rounded-md outline-none focus:border-pink-400" />
            <button onClick={submitAdd} disabled={adding || !form.handle.trim()}
              className="sm:col-span-2 px-3 py-2 bg-pink-600 text-white text-xs font-semibold rounded-md disabled:opacity-50 flex items-center justify-center gap-1"
              data-testid="doozyad-influencer-add-submit">
              {adding ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />} Save influencer
            </button>
          </div>
        )}
      </div>

      {influencers.length === 0 ? (
        <EmptyState icon={UserPlus} title="No influencers tracked yet" subtitle="Add an IG handle above to get started." />
      ) : (
        <div className="space-y-3">
          {influencers.map((inf) => (
            <InfluencerCard key={inf.id} inf={inf} authAxios={authAxios} onChange={onRefresh} />
          ))}
        </div>
      )}
    </div>
  );
};

export const InfluencerCard = ({ inf, authAxios, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [showDM, setShowDM] = useState(!!inf.dm_message);
  const [updating, setUpdating] = useState(false);

  const generateDM = async () => {
    setBusy(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/influencers/${inf.id}/generate-dm`, null, { timeout: 60000 });
      await onChange();
      setShowDM(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "DM generation failed");
    }
    setBusy(false);
  };

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await authAxios.post(`${API_URL}/api/admin/doozyad/influencers/${inf.id}/update-status`, { status: newStatus });
      await onChange();
    } catch (e) {
      alert(e?.response?.data?.detail || "Update failed");
    }
    setUpdating(false);
  };

  const remove = async () => {
    if (!window.confirm(`Remove @${inf.handle} from tracker?`)) return;
    setBusy(true);
    try {
      await authAxios.delete(`${API_URL}/api/admin/doozyad/influencers/${inf.id}`);
      await onChange();
    } catch { /* ignore */ }
    setBusy(false);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid={`doozyad-influencer-${inf.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[#1E1B4B]">@{inf.handle}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[inf.status] || "bg-gray-100"}`}>
              {inf.status}
            </span>
          </div>
          <div className="text-[11px] text-[#64748B]">
            {inf.name || "—"} · {inf.niche} · {inf.follower_count ? `${(inf.follower_count / 1000).toFixed(0)}K followers` : "?"} · {inf.location_city || "?"}
          </div>
        </div>
        <button onClick={remove} disabled={busy}
          className="p-1.5 text-gray-400 hover:text-rose-500 hover:bg-rose-50 rounded">
          <Trash2 size={13} />
        </button>
      </div>

      {inf.dm_message && showDM && (
        <div className="mt-2 bg-pink-50 border border-pink-100 rounded-lg p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-pink-700 mb-1">Cold DM draft</div>
          <pre className="text-xs text-[#1E1B4B] whitespace-pre-wrap leading-relaxed font-sans">{inf.dm_message}</pre>
          {inf.opener_alt && (
            <div className="mt-2 pt-2 border-t border-pink-100">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-pink-700 mb-1">Short opener (alt)</div>
              <pre className="text-xs text-[#64748B] whitespace-pre-wrap font-sans">{inf.opener_alt}</pre>
            </div>
          )}
          {inf.follow_up_message && (
            <div className="mt-2 pt-2 border-t border-pink-100">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-pink-700 mb-1">7-day follow-up</div>
              <pre className="text-xs text-[#64748B] whitespace-pre-wrap font-sans">{inf.follow_up_message}</pre>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2 mt-3 flex-wrap items-center">
        <button onClick={generateDM} disabled={busy}
          className="px-3 py-2 bg-pink-600 text-white text-xs font-semibold rounded-lg disabled:opacity-50 flex items-center gap-1"
          data-testid={`doozyad-influencer-gen-dm-${inf.id}`}>
          {busy ? <Loader2 size={13} className="animate-spin" /> : <MessageCircle size={13} />}
          {inf.dm_message ? "Regenerate DM" : "Generate cold DM"}
        </button>
        {inf.dm_message && (
          <button onClick={() => setShowDM(!showDM)}
            className="px-3 py-2 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200">
            {showDM ? "Hide" : "Show"} DM
          </button>
        )}
        <select value={inf.status} onChange={(e) => updateStatus(e.target.value)} disabled={updating}
          className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white disabled:opacity-50"
          data-testid={`doozyad-influencer-status-${inf.id}`}>
          {STATUSES.map((s) => <option key={s} value={s}>Status: {s}</option>)}
        </select>
      </div>
    </div>
  );
};


// ──────────────────────────────────────────────────────────────────────
// Command Center — Phase 3 capstone overview
// ──────────────────────────────────────────────────────────────────────

